import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { sanitizeUserFacingErrorText, toastApiError } from "@/lib/errorMessages";
import type { HandshakeTimeSelectionMode } from "@/types";
import { handshakeService } from "@/services/handshakeService";
import { handshakeSessionDetailQueryKey } from "@/hooks/useHandshakeSessionDetail";

interface UseHandshakeSessionActionsOptions {
  admin: boolean;
  refreshBookings?: boolean;
}

export function useHandshakeSessionActions(
  sessionId: string,
  options: UseHandshakeSessionActionsOptions,
) {
  const { t } = useTranslation("handshakePages");
  const queryClient = useQueryClient();
  const [isSending, setIsSending] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isReopening, setIsReopening] = useState(false);

  const invalidateSession = async () => {
    await queryClient.invalidateQueries({
      queryKey: handshakeSessionDetailQueryKey(sessionId, options.admin),
    });
    if (options.refreshBookings) {
      await queryClient.invalidateQueries({ queryKey: ["bookings"] });
      await queryClient.invalidateQueries({ queryKey: ["booking"] });
    }
  };

  const sendMessage = async (content: string) => {
    setIsSending(true);
    try {
      const result = options.admin
        ? await handshakeService.adminSendMessage(sessionId, { content })
        : await handshakeService.sendMessage(sessionId, { content });
      if (result.blocked) {
        toast.error(
          sanitizeUserFacingErrorText(result.block_reason, t("messageBlocked")) ??
            t("messageBlocked"),
        );
      } else {
        toast.success(t("messageSent"));
      }
      await invalidateSession();
    } catch (error) {
      console.error("Failed to send handshake message:", error);
      toastApiError(error, "generic", t("sendMessageError"));
    } finally {
      setIsSending(false);
    }
  };

  const cancelSession = async () => {
    setIsCancelling(true);
    try {
      await handshakeService.cancelSession(
        sessionId,
        options.admin ? t("cancelReasonAdmin") : t("cancelReasonPartner"),
      );
      toast.success(t("conversationCancelled"));
      await invalidateSession();
      return true;
    } catch (error) {
      console.error("Failed to cancel handshake session:", error);
      toastApiError(error, "generic", t("cancelConversationError"));
      return false;
    } finally {
      setIsCancelling(false);
    }
  };

  const confirmSession = async (payload: {
    visit_at: string;
    time_slot: string;
    time_selection_mode: HandshakeTimeSelectionMode;
  }) => {
    setIsConfirming(true);
    try {
      if (options.admin) {
        await handshakeService.adminConfirmSession(sessionId, payload);
      } else {
        await handshakeService.confirmSession(sessionId, payload);
      }
      toast.success(t("confirmBookingSuccess"));
      await invalidateSession();
      return true;
    } catch (error) {
      console.error("Failed to confirm handshake session:", error);
      toastApiError(error, "generic", t("confirmBookingError"));
      return false;
    } finally {
      setIsConfirming(false);
    }
  };

  const reopenSession = async () => {
    setIsReopening(true);
    try {
      if (options.admin) {
        await handshakeService.adminReopenSession(sessionId);
      } else {
        await handshakeService.reopenSession(sessionId);
      }
      toast.success(t("reopenSessionSuccess"));
      await invalidateSession();
      return true;
    } catch (error) {
      console.error("Failed to reopen handshake session:", error);
      toastApiError(error, "generic", t("reopenSessionError"));
      return false;
    } finally {
      setIsReopening(false);
    }
  };

  return {
    sendMessage,
    cancelSession,
    confirmSession,
    reopenSession,
    invalidateSession,
    isSending,
    isCancelling,
    isConfirming,
    isReopening,
  };
}
